import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Login from "./Components/Home/Login";
import AppStateScreen from "./Components/common/AppStateScreen";
import AppErrorBoundary from "./Components/common/AppErrorBoundary";
import { AuthProvider, useAuth } from "./contexts/AuthContext.jsx";
import LoadingContext from "./contexts/LoadingContext.js";
import { readAuthToken } from "./utils/parentPortalAuth.js";
import {
  isManagementRole,
  normalizeSystemRoleValue,
  resolveDashboardRoute,
} from "./utils/rbac.js";
import { getManagementRouteEntriesForRole } from "./config/managementRouteRegistry.js";
import "./App.css";

const SuperAdminDashboard = lazy(
  () => import("./Components/SuperAdmin/SuperAdminDashboard"),
);
const ParentDashboard = lazy(
  () => import("./Components/Parent/ParentDashboard"),
);
const Fees = lazy(() => import("./Components/Admin/Fees"));
const FinanceDocumentView = lazy(
  () => import("./Components/Admin/Accounting/FinanceDocumentView.jsx"),
);
const TeacherPortalLayout = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherPortalLayout,
  })),
);
const TeacherHomePage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherHomePage,
  })),
);
const TeacherAttendancePage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherAttendancePage,
  })),
);
const TeacherHomeworkPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherHomeworkPage,
  })),
);
const TeacherZeroPeriodPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherZeroPeriodPage,
  })),
);
const TeacherZeroPeriodTestPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherZeroPeriodTestPage,
  })),
);
const TeacherTimetablePage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherTimetablePage,
  })),
);
const TeacherCalendarPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherCalendarPage,
  })),
);
const TeacherWeeklyPlanPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherWeeklyPlanPage,
  })),
);
const TeacherClassesIndexPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherClassesIndexPage,
  })),
);
const TeacherClassesPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherClassesPage,
  })),
);
const TeacherStudentProfilePage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherStudentProfilePage,
  })),
);
const TeacherNotificationsPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherNotificationsPage,
  })),
);
const TeacherCommunicationPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherCommunicationPage,
  })),
);
const TeacherResultsPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherResultsPage,
  })),
);
const TeacherProfilePage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherProfilePage,
  })),
);
const TeacherPayrollPage = lazy(() =>
  import("./Components/Teacher/TeacherPortalRoutes.jsx").then((mod) => ({
    default: mod.TeacherPayrollPage,
  })),
);
const ForgotPsw = lazy(() => import("./Components/common/ForgetPsw"));
const AdmissionForm = lazy(() => import("./Components/Guest/AdmissionForm"));
const RoleDirectorDashboard = lazy(() =>
  import("./Components/Dashboards/RoleDashboards.jsx").then((mod) => ({
    default: mod.DirectorDashboard,
  })),
);
const RolePrincipalDashboard = lazy(() =>
  import("./Components/Dashboards/RoleDashboards.jsx").then((mod) => ({
    default: mod.PrincipalDashboard,
  })),
);
const RoleVpDashboard = lazy(() =>
  import("./Components/Dashboards/RoleDashboards.jsx").then((mod) => ({
    default: mod.VpDashboard,
  })),
);
const RoleCoordinatorDashboard = lazy(() =>
  import("./Components/Dashboards/RoleDashboards.jsx").then((mod) => ({
    default: mod.CoordinatorDashboard,
  })),
);
const RoleReceptionDashboard = lazy(() =>
  import("./Components/Dashboards/RoleDashboards.jsx").then((mod) => ({
    default: mod.ReceptionDashboard,
  })),
);
const RoleAccountantDashboard = lazy(() =>
  import("./Components/Dashboards/RoleDashboards.jsx").then((mod) => ({
    default: mod.AccountantDashboard,
  })),
);
const RoleManagementFallbackDashboard = lazy(() =>
  import("./Components/Dashboards/RoleDashboards.jsx").then((mod) => ({
    default: mod.ManagementFallbackDashboard,
  })),
);

const RBAC_NOTICE_KEY = "rbac_redirect_notice";
const parentPortalPath = "/parent/*";
const parentPortalRedirectMap = {
  "/parent": "/parent/home",
  "/portal": "/parent/home",
  "/home": "/parent/home",
  "/homework": "/parent/homework",
  "/school-week": "/parent/school-week",
  "/timetable": "/parent/school-week",
  "/course-plan": "/parent/school-week",
  "/calendar": "/parent/calendar",
  "/meet-school": "/parent/messages",
  "/fees": "/parent/fees",
  "/profile": "/parent/profile",
  "/portal/home": "/parent/home",
  "/portal/homework": "/parent/homework",
  "/portal/school-week": "/parent/school-week",
  "/portal/timetable": "/parent/school-week",
  "/portal/course-plan": "/parent/school-week",
  "/portal/calendar": "/parent/calendar",
  "/portal/meet-school": "/parent/messages",
  "/portal/fees": "/parent/fees",
  "/portal/profile": "/parent/profile",
  "/notices": "/parent/messages",
  "/messages": "/parent/messages",
  "/attendance": "/parent/home",
  "/weekly-plan": "/parent/school-week",
  "/lost-found": "/parent/home",
  "/portal/notices": "/parent/messages",
  "/portal/messages": "/parent/messages",
  "/portal/attendance": "/parent/home",
  "/portal/weekly-plan": "/parent/school-week",
  "/portal/lost-found": "/parent/home",
  "/student": "/parent/home",
  "/student/*": "/parent/home",
};

const teacherModuleRoutes = [
  { path: "home", element: <TeacherHomePage /> },
  { path: "attendance", element: <TeacherAttendancePage /> },
  { path: "homework", element: <TeacherHomeworkPage /> },
  { path: "zero-period", element: <TeacherZeroPeriodPage /> },
  { path: "zero-period-test", element: <TeacherZeroPeriodTestPage /> },
  { path: "timetable", element: <TeacherTimetablePage /> },
  { path: "calendar", element: <TeacherCalendarPage /> },
  { path: "weekly-plan", element: <TeacherWeeklyPlanPage /> },
  { path: "classes", element: <TeacherClassesIndexPage /> },
  { path: "classes/:id", element: <TeacherClassesPage /> },
  { path: "students/:id", element: <TeacherStudentProfilePage /> },
  { path: "results", element: <TeacherResultsPage /> },
  { path: "messages", element: <TeacherCommunicationPage /> },
  { path: "notifications", element: <TeacherNotificationsPage /> },
  { path: "payroll", element: <TeacherPayrollPage /> },
  { path: "expenses", element: <Navigate to="/teacher/payroll" replace /> },
  { path: "salary", element: <Navigate to="/teacher/payroll" replace /> },
  { path: "profile", element: <TeacherProfilePage /> },
];

const directorModuleRoutes = getManagementRouteEntriesForRole("director");
const principalModuleRoutes = getManagementRouteEntriesForRole("principal");
const vpModuleRoutes = getManagementRouteEntriesForRole("vp");
const coordinatorModuleRoutes = getManagementRouteEntriesForRole("coordinator");
const receptionModuleRoutes = getManagementRouteEntriesForRole("reception");
const accountantModuleRoutes = getManagementRouteEntriesForRole("accountant");
const fallbackManagementRoutes = getManagementRouteEntriesForRole("management");

const writeRbacNotice = (payload = {}) => {
  try {
    localStorage.setItem(RBAC_NOTICE_KEY, JSON.stringify(payload));
  } catch (error) {
    // ignore
  }
};

const ProtectedRoleRoute = ({ allowRole, children }) => {
  const { loading, systemRole } = useAuth();
  const loginRoute = allowRole === "teacher" ? "/teacher/login" : "/login";
  if (!readAuthToken()) return <Navigate to={loginRoute} replace />;
  if (loading) {
    return (
      <AppStateScreen
        title="Loading portal"
        message="Checking your access..."
      />
    );
  }
  const requiredSystemRole = allowRole === "teacher" ? "TEACHER" : "";
  if (normalizeSystemRoleValue(systemRole) !== requiredSystemRole)
    return <Navigate to="/" replace />;
  return children;
};

const ForbiddenScreen = ({ message, onBack }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };
  return (
    <AppStateScreen
      tone="error"
      title="403 Forbidden"
      message={message || "You don't have access to this page."}
      actionLabel={onBack ? "Back to my dashboard" : undefined}
      onAction={onBack}
      secondaryActionLabel="Log out"
      onSecondaryAction={handleLogout}
    />
  );
};

const RequireSystemRole = ({ requiredRole, children }) => {
  const { loading, systemRole } = useAuth();
  const navigate = useNavigate();

  if (!readAuthToken()) {
    return <Navigate to="/login" replace />;
  }
  if (loading) {
    return (
      <AppStateScreen
        title="Loading portal"
        message="Checking your access..."
      />
    );
  }
  const normalizedRequired = normalizeSystemRoleValue(requiredRole);
  const normalizedSystem = normalizeSystemRoleValue(systemRole);
  if (normalizedSystem !== normalizedRequired) {
    return (
      <ForbiddenScreen onBack={() => navigate("/login", { replace: true })} />
    );
  }
  return children;
};

const RequireManagementRole = ({ requiredRole, children }) => {
  const { loading, systemRole, managementRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const roleToken = String(requiredRole || "")
    .trim()
    .toUpperCase();
  const normalizedManagement = String(managementRole || "")
    .trim()
    .toUpperCase();
  const isAllowed = normalizedManagement === roleToken;
  const nextPath = resolveDashboardRoute({ systemRole, managementRole });
  const canRedirect =
    nextPath && nextPath !== "/login" && nextPath !== location.pathname;

  useEffect(() => {
    if (loading) return;
    if (!readAuthToken()) return;
    if (isAllowed) return;
    if (canRedirect) {
      writeRbacNotice({
        message: "Redirected to your dashboard.",
        tone: "info",
      });
      navigate(nextPath, { replace: true });
    }
  }, [loading, isAllowed, canRedirect, nextPath, navigate]);

  if (!readAuthToken()) {
    return <Navigate to="/login" replace />;
  }
  if (loading) {
    return (
      <AppStateScreen
        title="Loading portal"
        message="Checking your access..."
      />
    );
  }
  if (!isAllowed) {
    if (canRedirect) {
      return (
        <AppStateScreen
          title="Redirecting"
          message="Taking you to the correct dashboard..."
        />
      );
    }
    const backTarget = nextPath && nextPath !== "/login" ? nextPath : "/login";
    const message =
      systemRole === "ADMIN" &&
      (!managementRole || managementRole === "unknown")
        ? "Role not configured. Contact SuperAdmin to assign your management role."
        : "You don't have access to this dashboard.";
    return (
      <ForbiddenScreen
        message={message}
        onBack={() => navigate(backTarget, { replace: true })}
      />
    );
  }
  return children;
};

const RoleRedirect = () => {
  const navigate = useNavigate();
  const { loading, systemRole, managementRole, sessionUnavailable, refresh } =
    useAuth();

  useEffect(() => {
    if (loading || sessionUnavailable) return;
    const nextPath = resolveDashboardRoute({ systemRole, managementRole });
    if (nextPath && nextPath !== "/login") {
      if (
        nextPath === "/dashboard/management" &&
        systemRole === "ADMIN" &&
        (!managementRole || managementRole === "unknown")
      ) {
        writeRbacNotice({
          message:
            "Role not configured. You have safe access to Home and Messages only. Contact SuperAdmin.",
          tone: "error",
        });
      }
      navigate(nextPath, { replace: true });
      return;
    }
    navigate("/login", { replace: true });
  }, [loading, sessionUnavailable, systemRole, managementRole, navigate]);

  if (sessionUnavailable && readAuthToken()) {
    return (
      <AppStateScreen
        title="Connection needed"
        message="We couldn't check your session. Check your connection and retry. You have not been logged out."
        actionLabel="Retry"
        onAction={() => refresh({ force: true })}
      />
    );
  }

  return (
    <AppStateScreen
      title="Redirecting"
      message="Taking you to the correct dashboard..."
    />
  );
};

const AdminFeesRoute = () => {
  const navigate = useNavigate();
  const { loading, systemRole, managementRole, school, schoolId } = useAuth();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!readAuthToken()) {
      setChecked(true);
      navigate("/login", { replace: true });
      return;
    }
    try {
      const role = String(managementRole || "")
        .trim()
        .toLowerCase();
      if (
        normalizeSystemRoleValue(systemRole) !== "ADMIN" ||
        !isManagementRole(role)
      ) {
        setChecked(true);
        navigate("/", { replace: true });
        return;
      }
      setChecked(true);
    } catch (error) {
      setChecked(true);
      navigate("/login", { replace: true });
    }
  }, [loading, managementRole, navigate, systemRole]);

  if (!checked) {
    return (
      <AppStateScreen
        title="Loading portal"
        message="Checking your access..."
      />
    );
  }
  const adminId = schoolId || "";
  if (!adminId) {
    return (
      <AppStateScreen
        title="Redirecting"
        message="Taking you to the correct portal..."
      />
    );
  }
  return <Fees adminId={adminId} schoolName={school?.name || ""} />;
};

const ParentPortalRoute = () => {
  const { loading, systemRole, sessionUnavailable, refresh } = useAuth();
  if (!readAuthToken()) return <Navigate to="/login" replace />;
  if (loading) {
    return (
      <AppStateScreen
        title="Loading portal"
        message="Checking your access..."
      />
    );
  }
  if (sessionUnavailable && !systemRole) {
    return (
      <AppStateScreen
        title="Connection needed"
        message="We couldn't load the Parent portal. Check your connection and retry. You have not been logged out."
        actionLabel="Retry"
        onAction={() => refresh({ force: true })}
      />
    );
  }
  if (normalizeSystemRoleValue(systemRole) !== "STUDENT")
    return <Navigate to="/" replace />;
  return <ParentDashboard />;
};

const App = () => {
  const [isLoading, setIsLoading] = useState(false);
  const toggleLoading = (value) => {
    setIsLoading(value);
  };
  const appFallback = (
    <AppStateScreen
      title="Loading portal"
      message="Preparing your dashboard..."
    />
  );
  return (
    <LoadingContext.Provider value={{ isLoading, toggleLoading }}>
      <div className={`${isLoading ? "opacity-50 " : ""}`}>
        <AppErrorBoundary>
          <AuthProvider>
            <Router>
              <Suspense fallback={appFallback}>
                <Routes>
                  <Route path="/" element={<Login />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/teacher/login" element={<Login />} />
                  <Route path="/admin" element={<RoleRedirect />} />
                  <Route path="/dashboard" element={<RoleRedirect />} />
                  <Route
                    path="/dashboard/management"
                    element={
                      <RequireSystemRole requiredRole="ADMIN">
                        <RoleManagementFallbackDashboard />
                      </RequireSystemRole>
                    }
                  />
                  {fallbackManagementRoutes.map((route) => (
                    <Route
                      key={`management-${route.path}`}
                      path={`/dashboard/management/${route.path}`}
                      element={
                        <RequireSystemRole requiredRole="ADMIN">
                          <RoleManagementFallbackDashboard
                            initialViewKey={route.key}
                          />
                        </RequireSystemRole>
                      }
                    />
                  ))}
                  <Route
                    path="/dashboard/director"
                    element={
                      <RequireManagementRole requiredRole="DIRECTOR">
                        <RoleDirectorDashboard />
                      </RequireManagementRole>
                    }
                  />
                  {directorModuleRoutes.map((route) => (
                    <Route
                      key={`director-${route.path}`}
                      path={`/dashboard/director/${route.path}`}
                      element={
                        <RequireManagementRole requiredRole="DIRECTOR">
                          <RoleDirectorDashboard initialViewKey={route.key} />
                        </RequireManagementRole>
                      }
                    />
                  ))}
                  <Route
                    path="/dashboard/principal"
                    element={
                      <RequireManagementRole requiredRole="PRINCIPAL">
                        <RolePrincipalDashboard />
                      </RequireManagementRole>
                    }
                  />
                  {principalModuleRoutes.map((route) => (
                    <Route
                      key={`principal-${route.path}`}
                      path={`/dashboard/principal/${route.path}`}
                      element={
                        <RequireManagementRole requiredRole="PRINCIPAL">
                          <RolePrincipalDashboard initialViewKey={route.key} />
                        </RequireManagementRole>
                      }
                    />
                  ))}
                  <Route
                    path="/dashboard/vp"
                    element={
                      <RequireManagementRole requiredRole="VP">
                        <RoleVpDashboard />
                      </RequireManagementRole>
                    }
                  />
                  {vpModuleRoutes.map((route) => (
                    <Route
                      key={`vp-${route.path}`}
                      path={`/dashboard/vp/${route.path}`}
                      element={
                        <RequireManagementRole requiredRole="VP">
                          <RoleVpDashboard initialViewKey={route.key} />
                        </RequireManagementRole>
                      }
                    />
                  ))}
                  <Route
                    path="/dashboard/coordinator"
                    element={
                      <RequireManagementRole requiredRole="COORDINATOR">
                        <RoleCoordinatorDashboard />
                      </RequireManagementRole>
                    }
                  />
                  {coordinatorModuleRoutes.map((route) => (
                    <Route
                      key={`coordinator-${route.path}`}
                      path={`/dashboard/coordinator/${route.path}`}
                      element={
                        <RequireManagementRole requiredRole="COORDINATOR">
                          <RoleCoordinatorDashboard
                            initialViewKey={route.key}
                          />
                        </RequireManagementRole>
                      }
                    />
                  ))}
                  <Route
                    path="/dashboard/reception"
                    element={
                      <RequireManagementRole requiredRole="RECEPTION">
                        <RoleReceptionDashboard />
                      </RequireManagementRole>
                    }
                  />
                  {receptionModuleRoutes.map((route) => (
                    <Route
                      key={`reception-${route.path}`}
                      path={`/dashboard/reception/${route.path}`}
                      element={
                        <RequireManagementRole requiredRole="RECEPTION">
                          <RoleReceptionDashboard initialViewKey={route.key} />
                        </RequireManagementRole>
                      }
                    />
                  ))}
                  <Route
                    path="/dashboard/accountant"
                    element={
                      <RequireManagementRole requiredRole="ACCOUNTANT">
                        <RoleAccountantDashboard />
                      </RequireManagementRole>
                    }
                  />
                  {accountantModuleRoutes.map((route) => (
                    <Route
                      key={`accountant-${route.path}`}
                      path={`/dashboard/accountant/${route.path}`}
                      element={
                        <RequireManagementRole requiredRole="ACCOUNTANT">
                          <RoleAccountantDashboard initialViewKey={route.key} />
                        </RequireManagementRole>
                      }
                    />
                  ))}
                  <Route path="/admin/fees" element={<AdminFeesRoute />} />
                  <Route
                    path="/finance/documents/:schoolId/:documentType/:documentId"
                    element={
                      <RequireSystemRole requiredRole="ADMIN">
                        <FinanceDocumentView />
                      </RequireSystemRole>
                    }
                  />
                  <Route
                    path="/superadmin/*"
                    element={
                      <RequireSystemRole requiredRole="SUPERADMIN">
                        <SuperAdminDashboard />
                      </RequireSystemRole>
                    }
                  />
                  <Route
                    path="/teacher"
                    element={
                      <ProtectedRoleRoute allowRole="teacher">
                        <TeacherPortalLayout />
                      </ProtectedRoleRoute>
                    }
                  >
                    <Route index element={<Navigate to="home" replace />} />
                    {teacherModuleRoutes.map((route) => (
                      <Route
                        key={`teacher-${route.path}`}
                        path={route.path}
                        element={route.element}
                      />
                    ))}
                    <Route path="*" element={<Navigate to="home" replace />} />
                  </Route>
                  <Route
                    path={parentPortalPath}
                    element={<ParentPortalRoute />}
                  />
                  {Object.entries(parentPortalRedirectMap).map(([from, to]) => (
                    <Route
                      key={from}
                      path={from}
                      element={<Navigate to={to} replace />}
                    />
                  ))}
                  <Route path="/forget" element={<ForgotPsw />} />
                  <Route path="/admissions/apply" element={<AdmissionForm />} />
                  <Route path="*" element={<Navigate to="/login" replace />} />

                  {/* <Route path="/edit/:id" element={<StudentEdit/>} /> */}
                </Routes>
              </Suspense>
            </Router>
          </AuthProvider>
        </AppErrorBoundary>
      </div>
      {isLoading && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <CircularProgress /> {/* Center the CircularProgress loader */}
        </div>
      )}
    </LoadingContext.Provider>
  );
};

export default App;
export { default as LoadingContext } from "./contexts/LoadingContext.js";
